// src/import/GstCalculation.js
// Calculates item-wise GST and bill totals based ONLY on customer's regionType.
// Local  -> CGST + SGST
// Outside-> IGST

let gstDetails = [];

export const GstCalculation = (
  Party,
  product = [],
  Context = {},
  discount = []
) => {
  // ---------- helpers ----------
  const roundHalfUp = (num) => {
    const frac = num - Math.floor(num);
    return frac > 0.49 ? Math.ceil(num) : Math.floor(num);
  };

  // region selector (single source of truth)
  const region = String(Party?.regionType || "Local").toLowerCase();
  const isIGST = region === "outside";

  // bill-level charges % needs the highest GST% among items
  const highestItemGST = product.reduce((m, p) => {
    const g = Number(
      p?.productData?.GSTRate || p?.productId?.GSTRate || p?.gstPercentage || 0
    );
    return isNaN(g) ? m : Math.max(m, g);
  }, 0);

  // split discounts array into pure “Discount” vs “Charges”
  const billDiscounts = (Array.isArray(discount) ? discount : []).filter((d) =>
    String(d?.type || "")
      .toLowerCase()
      .includes("discount")
  );
  const billCharges = (Array.isArray(discount) ? discount : []).filter((d) =>
    String(d?.type || "")
      .toLowerCase()
      .includes("charge")
  );

  // ---------- per-item rows ----------
  gstDetails = (product || []).map((row) => {
    const ratePct = Number(
      row?.productData?.GSTRate ||
        row?.productId?.GSTRate ||
        row?.gstPercentage ||
        0
    );
    const qty = Number(row?.qty || 0);
    const basic = Number(row?.basicPrice ?? row?.price ?? 0); // net-of-tax basic per unit
    const taxable = Number((basic * qty).toFixed(2));

    // Spread bill-level % discounts across items (if you pass only discounts here).
    let discountValue = 0;
    billDiscounts.forEach((d) => {
      const isPct = String(d?.discounttype).toLowerCase() === "percentage";
      const pct = Number(d?.percentage || 0);
      const amt = Number(d?.amount || d?.discountedValue || 0);
      discountValue += isPct
        ? (taxable * pct) / 100
        : amt / (product.length || 1);
    });
    const netTaxable = Math.max(0, taxable - discountValue);

    let cgstRate = 0,
      sgstRate = 0,
      igstRate = 0;
    if (isIGST) {
      igstRate = Number(((netTaxable * ratePct) / 100).toFixed(2));
    } else {
      const half = ratePct / 2;
      cgstRate = Number(((netTaxable * half) / 100).toFixed(2));
      sgstRate = Number(((netTaxable * half) / 100).toFixed(2));
    }

    const grandTotal = Number(
      (netTaxable + cgstRate + sgstRate + igstRate).toFixed(2)
    );

    return {
      // for HSN grouping (optional)
      hsn: row?.productData?.HSN_Code,

      // display helpers
      gstPercentage: isIGST ? ratePct : `${ratePct / 2}+${ratePct / 2}`,

      // row amounts
      taxable: Number(netTaxable.toFixed(2)),
      cgstRate,
      sgstRate,
      igstRate,
      grandTotal,

      // legacy keys you were reading
      centralTax: cgstRate ? [{ rate: ratePct / 2, amount: cgstRate }] : [],
      stateTax: sgstRate ? [{ rate: ratePct / 2, amount: sgstRate }] : [],
      igstTax: igstRate ? [{ rate: ratePct, amount: igstRate }] : [],
    };
  });

  // ---------- totals (items only) ----------
  const Amount = gstDetails.reduce((s, r) => s + (r.taxable || 0), 0); // basic total AFTER discounts
  const CgstItems = gstDetails.reduce((s, r) => s + (r.cgstRate || 0), 0);
  const SgstItems = gstDetails.reduce((s, r) => s + (r.sgstRate || 0), 0);
  const IgstItems = gstDetails.reduce((s, r) => s + (r.igstRate || 0), 0);

  // ---------- bill-level charges amount ----------
  const chargesTotal = billCharges.reduce((sum, d) => {
    const isPct = String(d?.discounttype).toLowerCase() === "percentage";
    const pct = Number(d?.percentage || 0);
    const amt = Number(d?.amount || d?.discountedValue || 0);
    return sum + (isPct ? (Amount * pct) / 100 : amt);
  }, 0);

  // GST on charges (kept separate; NOT merged into item GST)
  let ChargesCgst = 0,
    ChargesSgst = 0,
    ChargesIgst = 0;
  if (chargesTotal > 0 && highestItemGST > 0) {
    if (isIGST) {
      ChargesIgst = (chargesTotal * highestItemGST) / 100;
    } else {
      const half = highestItemGST / 2;
      ChargesCgst = (chargesTotal * half) / 100;
      ChargesSgst = (chargesTotal * half) / 100;
    }
  }

  // ----------- Total Before Tax (items after discounts + charges amount)
  const TotalBeforeTax = Amount + chargesTotal;

  // ----------- Grand = TBT + charges GST (ONLY) -> rounded half up
  const gross = TotalBeforeTax + ChargesCgst + ChargesSgst + ChargesIgst;
  const Round = roundHalfUp(gross);
  const RoundOff = Number(Math.abs(Round - gross).toFixed(2));

  const Tax = {
    IgstTaxType: isIGST,

    // items-only basics & taxes
    Amount: Number(Amount.toFixed(2)),
    CgstTotal: Number(CgstItems.toFixed(2)),
    SgstTotal: Number(SgstItems.toFixed(2)),
    IgstTotal: Number(IgstItems.toFixed(2)),

    // charges GST kept separate
    ChargesCgst: Number(ChargesCgst.toFixed(2)),
    ChargesSgst: Number(ChargesSgst.toFixed(2)),
    ChargesIgst: Number(ChargesIgst.toFixed(2)),

    // totals per your formula
    TotalBeforeTax: Number(TotalBeforeTax.toFixed(2)),
    RoundOff,
    GrandTotal: Number(Round.toFixed(2)),
  };

  return { gstDetails, Tax };
};
